/* -------------------------------------------------------
   AnswerMetaPanels — Collapsible data-transparency panels
   shown below completed assistant answers.
   Sections: Data Sources, Assumptions, Confidence, Debug
   ------------------------------------------------------- */

import React, { useState } from 'react';
import type { DataSource, DebugInfo } from '../../types/chatV2';

/* ---- generic collapsible section ---- */

const Section: React.FC<{
  title: string;
  icon: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, icon, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={`cv2-meta-section ${open ? 'cv2-meta-section--open' : ''}`}>
      <button
        className="cv2-meta-section-header"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={`${open ? 'Collapse' : 'Expand'} ${title}`}
      >
        <span>
          {icon ? `${icon} ` : ''}{title}
        </span>
        <span className="cv2-meta-chevron">{open ? '\u25BE' : '\u25B8'}</span>
      </button>
      {open && <div className="cv2-meta-section-body">{children}</div>}
    </div>
  );
};

/* ---- confidence meter ---- */

const ConfidenceMeter: React.FC<{ value: number }> = ({ value }) => {
  const pct = Math.round(value * 100);
  const color = pct >= 80 ? '#10b981' : pct >= 50 ? '#f59e0b' : '#ef4444';

  return (
    <div className="cv2-confidence">
      <div className="cv2-confidence-bar">
        <div
          className="cv2-confidence-fill"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      <span className="cv2-confidence-label" style={{ color }}>
        {pct}%
      </span>
    </div>
  );
};

/* ---- main component ---- */

interface Props {
  dataSources?: DataSource[];
  assumptions?: string[];
  confidence?: number;
  debug?: DebugInfo;
  showDebug: boolean;
}

const AnswerMetaPanels: React.FC<Props> = ({
  dataSources,
  assumptions,
  confidence,
  debug,
  showDebug,
}) => {
  const hasAnything =
    (dataSources && dataSources.length > 0) ||
    (assumptions && assumptions.length > 0) ||
    confidence != null ||
    (showDebug && debug);

  if (!hasAnything) return null;

  return (
    <div className="cv2-meta">
      {/* Data Sources */}
      {dataSources && dataSources.length > 0 && (
        <Section title="Data Sources" icon="">
          <div className="cv2-meta-sources">
            {dataSources.map((ds, i) => (
              <div key={i} className="cv2-meta-source-row">
                <span className="cv2-meta-source-name">{ds.toolName}</span>
                <span className="cv2-meta-source-detail">
                  v{ds.snapshotVersion} &middot;{' '}
                  {new Date(ds.timestamp).toLocaleDateString()}
                </span>
                {ds.fieldsUsed.length > 0 && (
                  <span className="cv2-meta-source-fields">
                    Fields: {ds.fieldsUsed.join(', ')}
                  </span>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Assumptions */}
      {assumptions && assumptions.length > 0 && (
        <Section title="Assumptions" icon="">
          <ul className="cv2-meta-assumptions">
            {assumptions.map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
        </Section>
      )}

      {/* Confidence */}
      {confidence != null && (
        <Section title="Confidence" icon="">
          <ConfidenceMeter value={confidence} />
        </Section>
      )}

      {/* Debug (dev / demo only) */}
      {showDebug && debug && (
        <Section title="Debug Info" icon="">
          <div className="cv2-debug">
            {debug.plan && (debug.plan as unknown[]).length > 0 && (
              <div className="cv2-debug-block">
                <h5>Plan</h5>
                <pre>{JSON.stringify(debug.plan, null, 2)}</pre>
              </div>
            )}
            {debug.toolCalls && (debug.toolCalls as unknown[]).length > 0 && (
              <div className="cv2-debug-block">
                <h5>Tool Calls</h5>
                <pre>{JSON.stringify(debug.toolCalls, null, 2)}</pre>
              </div>
            )}
            {debug.timings && Object.keys(debug.timings).length > 0 && (
              <div className="cv2-debug-block">
                <h5>Timings</h5>
                <pre>{JSON.stringify(debug.timings, null, 2)}</pre>
              </div>
            )}
          </div>
        </Section>
      )}
    </div>
  );
};

export default React.memo(AnswerMetaPanels);
